import { Link } from 'react-router-dom';
import { Calendar, Gauge, Fuel, Settings, MapPin, MessageCircle } from 'lucide-react';
import { Car } from '../types';
import { BUSINESS_CONFIG, createWhatsAppUrl, formatCarInfo } from '../config/business';

interface CarCardProps {
  car: Car;
}

const CarCard = ({ car }: CarCardProps) => {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage: number) => {
    return new Intl.NumberFormat('es-US').format(mileage);
  };

  // WhatsApp contact function
  const contactSeller = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation
    const carInfo = formatCarInfo(car);
    const message = `${BUSINESS_CONFIG.messages.interested} ${carInfo}. Could you please provide more information?`;
    const whatsappUrl = createWhatsAppUrl(message);
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="card group hover:scale-105 transform transition-all duration-300">
      {/* Image Container */}
      <div className="relative overflow-hidden h-48 bg-gray-200">
        {car.images && car.images.length > 0 ? (
          <img
            src={car.images[0]}
            alt={`${car.make} ${car.model}`}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = `https://via.placeholder.com/400x300/e5e7eb/6b7280?text=${car.make}+${car.model}`;
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <div className="text-center">
              <MapPin className="h-12 w-12 mx-auto mb-2" />
              <p className="text-sm font-medium">{car.make} {car.model}</p>
            </div>
          </div>
        )}
        
        {/* Status Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            car.condition === 'New' 
              ? 'bg-green-100 text-green-800' 
              : car.condition === 'Certified Pre-Owned'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {car.condition}
          </span>
          
          {/* Sale Status Badge */}
          {car.saleStatus && (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              car.saleStatus === 'available' 
                ? 'bg-green-100 text-green-800' 
                : car.saleStatus === 'pending'
                ? 'bg-orange-100 text-orange-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {car.saleStatus === 'available' ? 'Disponible' : 
               car.saleStatus === 'pending' ? 'Pendiente' : 'Vendido'}
            </span>
          )}
        </div>

        {/* Price Badge */}
        <div className="absolute top-3 right-3">
          <span className="bg-white/90 backdrop-blur-sm text-gray-900 px-2 py-1 text-sm font-bold rounded-md">
            {formatPrice(car.price)}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Title */}
        <div className="mb-3">
          <h3 className="font-bold text-lg text-gray-900 group-hover:text-primary-600 transition-colors">
            {car.year} {car.make} {car.model}
          </h3>
          <p className="text-sm text-gray-600 capitalize">{car.color}</p>
        </div>

        {/* Specs */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-sm text-gray-600">
          <div className="flex items-center space-x-1">
            <Calendar className="h-4 w-4" />
            <span>{car.year}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Gauge className="h-4 w-4" />
            <span>{formatMileage(car.mileage)} mi</span>
          </div>
          <div className="flex items-center space-x-1">
            <Fuel className="h-4 w-4" />
            <span>{car.fuelType}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Settings className="h-4 w-4" />
            <span>{car.transmission}</span>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {car.description}
        </p>

        {/* Pending Status Message */}
        {car.saleStatus === 'pending' && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
            <p className="text-orange-800 text-sm font-medium text-center">
              🤝 This car is already under a contract
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Link
            to={`/car/${car.id}`}
            className={`flex-1 text-center py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              car.saleStatus === 'pending' 
                ? 'bg-gray-100 text-gray-600 border border-gray-300' 
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            {car.saleStatus === 'pending' ? 'View Details' : 'Ver Detalles'}
          </Link>
          
          {/* WhatsApp Contact Button */}
          <button
            onClick={contactSeller}
            className="flex-shrink-0 p-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            title="Contact via WhatsApp"
          >
            <MessageCircle className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CarCard;